import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Ensure a sentence ends with terminal punctuation (. ! ? … or closing quote/bracket after one).
 * Trims trailing whitespace first. Returns empty string for falsy input.
 */
export function ensureSentencePunctuation(text: string | null | undefined): string {
  if (!text) return "";
  const trimmed = text.replace(/\s+$/, "");
  if (!trimmed) return "";
  // Already ends with terminal punctuation (optionally wrapped by closing quote/bracket).
  if (/[.!?…](["'”’)\]]+)?$/.test(trimmed)) return trimmed;
  return `${trimmed}.`;
}

/**
 * Split a paragraph (typically why_fit) into short, punchy bullets.
 * Splits on sentence boundaries and semicolons, trims connective openers
 * ("However,", "Also,"), drops empties, and caps the number of bullets.
 */
export function splitToBullets(text: string | null | undefined, max = 5): string[] {
  if (!text) return [];
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (!cleaned) return [];
  // Split on sentence terminators or semicolons.
  const raw = cleaned.split(/(?<=[.!?])\s+|\s*;\s+/);
  const bullets: string[] = [];
  for (let piece of raw) {
    piece = piece
      .replace(/^\s*(and|but|however|also|plus|moreover|additionally|furthermore|meanwhile|then|so|because)[,\s]+/i, "")
      .trim();
    // Strip trailing terminal punctuation for a cleaner bullet look.
    piece = piece.replace(/[.!?…]+$/, "").trim();
    if (piece.length < 3) continue;
    // Capitalise first letter.
    piece = piece.charAt(0).toUpperCase() + piece.slice(1);
    bullets.push(piece);
    if (bullets.length >= max) break;
  }
  return bullets;
}

/**
 * Condense text to a concise version that fits within `max` characters.
 * Unlike CSS line-clamp (which cuts mid-sentence and shows ellipsis), this
 * truncates at the last full sentence that fits, or at a word boundary with a
 * trailing period. Already-concise text is returned untouched.
 */
export function condenseText(text: string | null | undefined, max = 180): string {
  if (!text) return "";
  const trimmed = text.replace(/\s+/g, " ").trim();
  if (!trimmed) return "";
  if (trimmed.length <= max) return ensureSentencePunctuation(trimmed);

  // Prefer the last full sentence that fits within `max`.
  const sentenceEnds = [...trimmed.matchAll(/(?<=[.!?])\s+/g)];
  let cut = -1;
  for (const m of sentenceEnds) {
    const idx = m.index! + m[0].length;
    if (idx <= max) cut = idx;
    else break;
  }
  if (cut > 0) return trimmed.slice(0, cut).trim();

  // No sentence boundary fits: cut at the last word boundary within `max`.
  const slice = trimmed.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const truncated = (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trim();
  return ensureSentencePunctuation(truncated);
}

/**
 * Parse a months_rationale string into a temperature/weather chip and a descriptive note.
 *
 * The AI is instructed to format months_rationale as:
 *   "[Month] expects [low-high°C / low-high°F]. [Brief weather/conditions note]"
 *
 * This helper extracts:
 *  - `tempLabel`: a short temp range like "18–24°C / 64–75°F" (no month prefix)
 *  - `description`: the remaining brief weather/conditions note (without the temperature clause)
 *  - `emoji`: a weather emoji inferred from temperature and/or keywords in the description
 *
 * Falls back gracefully when the input doesn't match the expected format.
 */
export function parseWeatherRationale(rationale: string | null | undefined): {
  tempLabel: string | null;
  description: string;
  emoji: string;
} {
  const text = (rationale || "").trim();
  if (!text) return { tempLabel: null, description: "", emoji: "🌤️" };

  // Match a temperature range with both °C and °F.
  // Accepts separators "/", "(", ",", " " between the C and F values, e.g.
  //   "18-24°C / 64-75°F"  |  "24-30°C (75-86°F)"  |  "24–30°C, 75–86°F"
  const tempRegex = /(-?\d{1,3})\s*[–—-]\s*(-?\d{1,3})\s*°\s*C\s*[/(,\s]+\s*(-?\d{1,3})\s*[–—-]\s*(-?\d{1,3})\s*°\s*F\)?/i;
  const match = text.match(tempRegex);

  let tempLabel: string | null = null;
  let high: number | null = null;
  let description = text;

  if (match) {
    high = parseInt(match[2], 10);
    tempLabel = `${match[1]}–${match[2]}°C`;

    // Remove the sentence containing the temperature clause to leave just the description.
    const sentences = text.split(/(?<=[.!?])\s+/);
    description = sentences.filter((s) => !tempRegex.test(s)).join(" ").trim();
  }

  // Pick an emoji from keywords first, then temperature.
  const lower = text.toLowerCase();
  let emoji = "🌤️";
  if (/\b(snow|snowy|blizzard|ski|alpine winter)\b/.test(lower)) emoji = "❄️";
  else if (/\b(storm|thunder|monsoon|hurricane|typhoon)\b/.test(lower)) emoji = "⛈️";
  else if (/\b(rain|rainy|wet|showers|drizzle|humid)\b/.test(lower)) emoji = "🌧️";
  else if (/\b(cloud|cloudy|overcast|grey|gray)\b/.test(lower)) emoji = "☁️";
  else if (/\b(sunny|sunshine|clear skies|hot|scorching|arid|desert)\b/.test(lower)) emoji = "☀️";
  else if (high !== null) {
    if (high >= 28) emoji = "☀️";
    else if (high >= 18) emoji = "🌤️";
    else if (high >= 8) emoji = "⛅";
    else if (high >= 0) emoji = "🌥️";
    else emoji = "❄️";
  }

  return { tempLabel, description, emoji };
}

/**
 * Normalize destination names for consistent comparison and storage.
 * - Lowercase
 * - Trim whitespace
 * - Remove duplicate spaces
 * - Strip diacritics
 * - Remove bracketed suffixes like "(NL)"
 * - Normalize punctuation to comma/space
 * - Map common synonyms/abbreviations
 */
export function normalizeDestinationName(name: string): string {
  let normalized = name
    .toLowerCase()
    .trim()
    // Remove diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove bracketed suffixes
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    // Collapse spaces
    .replace(/\s+/g, ' ')
    // Keep only alphanumeric, spaces, and commas
    .replace(/[^\w\s,]/g, '')
    .trim();

  // Map common synonyms/abbreviations
  const synonyms: Record<string, string> = {
    'us': 'united states',
    'usa': 'united states',
    'uk': 'united kingdom',
    'england': 'united kingdom',
    'scotland': 'united kingdom',
    'wales': 'united kingdom',
    'northern ireland': 'united kingdom',
    'uae': 'united arab emirates',
    'nl': 'netherlands',
  };

  // Replace full-word matches
  for (const [abbrev, full] of Object.entries(synonyms)) {
    const regex = new RegExp(`\\b${abbrev}\\b`, 'g');
    normalized = normalized.replace(regex, full);
  }

  return normalized;
}

// Map of departure cities to their countries (for common UK/EU cities)
const CITY_COUNTRY_MAP: Record<string, string> = {
  'london': 'united kingdom',
  'manchester': 'united kingdom',
  'birmingham': 'united kingdom',
  'edinburgh': 'united kingdom',
  'glasgow': 'united kingdom',
  'bristol': 'united kingdom',
  'cardiff': 'united kingdom',
  'belfast': 'united kingdom',
  'liverpool': 'united kingdom',
  'leeds': 'united kingdom',
  'newcastle': 'united kingdom',
  'nottingham': 'united kingdom',
  'sheffield': 'united kingdom',
  'southampton': 'united kingdom',
  'paris': 'france',
  'lyon': 'france',
  'marseille': 'france',
  'nice': 'france',
  'berlin': 'germany',
  'munich': 'germany',
  'frankfurt': 'germany',
  'hamburg': 'germany',
  'amsterdam': 'netherlands',
  'rotterdam': 'netherlands',
  'dublin': 'ireland',
  'cork': 'ireland',
  'madrid': 'spain',
  'barcelona': 'spain',
  'rome': 'italy',
  'milan': 'italy',
  'brussels': 'belgium',
  'vienna': 'austria',
  'zurich': 'switzerland',
  'geneva': 'switzerland',
  'lisbon': 'portugal',
  'porto': 'portugal',
  'copenhagen': 'denmark',
  'stockholm': 'sweden',
  'oslo': 'norway',
  'helsinki': 'finland',
  'warsaw': 'poland',
  'prague': 'czech republic',
  'budapest': 'hungary',
  'athens': 'greece',
};

// Regional pairs where train/ferry is a viable alternative to flying
const REGIONAL_PAIRS = [
  ['united kingdom', 'ireland'],
  ['united kingdom', 'france'],
  ['united kingdom', 'netherlands'],
  ['united kingdom', 'belgium'],
  ['france', 'belgium'],
  ['france', 'germany'],
  ['france', 'switzerland'],
  ['france', 'spain'],
  ['france', 'italy'],
  ['germany', 'netherlands'],
  ['germany', 'belgium'],
  ['germany', 'austria'],
  ['germany', 'switzerland'],
  ['germany', 'czech republic'],
  ['germany', 'poland'],
  ['germany', 'denmark'],
  ['italy', 'switzerland'],
  ['italy', 'austria'],
  ['spain', 'portugal'],
  ['netherlands', 'belgium'],
  ['austria', 'czech republic'],
  ['austria', 'hungary'],
  ['sweden', 'denmark'],
  ['sweden', 'norway'],
  ['norway', 'denmark'],
];

/**
 * Detect if a destination is domestic (same country) or regional (neighboring country)
 * where car/train/ferry might be a better option than flying.
 */
export function isDomesticOrRegional(
  departureCity: string, 
  destinationName: string
): { isDomestic: boolean; isRegional: boolean; suggestion: string | null } {
  const departureLower = departureCity.toLowerCase().trim();
  const departureCountry = CITY_COUNTRY_MAP[departureLower] || null;
  
  if (!departureCountry) {
    return { isDomestic: false, isRegional: false, suggestion: null };
  }
  
  // Extract country from destination (format: "City, Country")
  const destinationParts = destinationName.split(',');
  const destinationCountryRaw = destinationParts[destinationParts.length - 1]?.trim() || '';
  const destinationCountry = normalizeDestinationName(destinationCountryRaw);
  
  // Check if same country (domestic)
  if (destinationCountry === departureCountry) {
    return {
      isDomestic: true,
      isRegional: false,
      suggestion: '🚗 Consider driving or taking the train, often cheaper and more convenient for domestic trips!'
    };
  }
  
  // Check if neighboring/regional
  const isRegional = REGIONAL_PAIRS.some(
    pair => (pair[0] === departureCountry && pair[1] === destinationCountry) ||
            (pair[1] === departureCountry && pair[0] === destinationCountry)
  );
  
  if (isRegional) {
    return {
      isDomestic: false,
      isRegional: true,
      suggestion: '🚆 Train or ferry might be a great option for this nearby destination!'
    };
  }
  
  return { isDomestic: false, isRegional: false, suggestion: null };
}
